<!--@format-->
<template>
    <div class="header-footer-details-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ elementType }} Template Details</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="isLoading" />
                <div v-else class="form-details-container">
                    <div v-if="elementData.name" class="detail-container">
                        <label>{{ elementType }} Template Name</label>
                        <div>{{ elementData.name }}</div>
                    </div>
                    <div v-if="elementData.name" class="detail-container">
                        <label>Preview</label>
                        <div>
                            <img
                                v-show="elementData.previewImageUrl"
                                :src="elementData.previewImageUrl"
                            />
                            <header-placeholder-svg v-show="!elementData.previewImageUrl" />
                        </div>
                    </div>
                    <div v-if="elementData.vendor" class="detail-container">
                        <label>Vendor</label>
                        <div class="profile-container">
                            <app-profile-image
                                :src="elementData.vendor.logoUrl | companyLogoPlaceholder"
                            />
                            {{ elementData.vendor.name }}
                        </div>
                    </div>
                    <div
                        v-if="elementData.categories && elementData.categories.length > 0"
                        class="detail-container"
                    >
                        <label>Categories</label>
                        <div class="categories-container">
                            <p
                                v-for="(category, idx) in elementData.categories"
                                :key="`category-${idx}`"
                                class="category"
                            >
                                {{ category }}
                            </p>
                        </div>
                    </div>
                    <div v-if="!isClient" class="detail-container">
                        <label>Status</label>
                        <div
                            class="status-chip"
                            :class="elementData.isPublished ? 'published' : 'unpublished'"
                        >
                            {{ elementData.isPublished ? 'Published' : 'Unpublished' }}
                        </div>
                    </div>
                    <div v-if="elementData.publishedTo" class="detail-container">
                        <label>{{
                            isAgency ? 'Partners published to' : 'Plans published to'
                        }}</label>
                        <div>{{ elementData.publishedTo }}</div>
                    </div>
                    <div class="detail-container">
                        <label>Created Date</label>
                        <div>
                            <span class="weekday">{{
                                elementData.createdAt | momentWeekDay | UpperCaseString
                            }}</span>
                            <span class="date">{{
                                elementData.createdAt | momentDateMMMDYYYY | UpperCaseString
                            }}</span>
                        </div>
                    </div>
                    <div v-if="!isClient && elementData.publishedAt" class="detail-container">
                        <label>Published Date</label>
                        <div>
                            <span class="weekday">{{
                                elementData.publishedAt | momentWeekDay | UpperCaseString
                            }}</span>
                            <span class="date">{{
                                elementData.publishedAt | momentDateMMMDYYYY | UpperCaseString
                            }}</span>
                        </div>
                    </div>
                    <div class="detail-container">
                        <label>Last Modified Date</label>
                        <div>
                            <span class="weekday">{{
                                elementData.updatedAt | momentWeekDay | UpperCaseString
                            }}</span>
                            <span class="date">{{
                                elementData.updatedAt | momentDateMMMDYYYY | UpperCaseString
                            }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button @click="closeWidget">Done</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { capitalize } from 'lodash';
import { LandingPageSectionService } from '@/scripts/services/landing-page/landing-page-section-service';

const AppButton = () => import('@/components/common/app-button.vue');
const AppProfileImage = () => import('@/components/common/app-profile-image');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');
const HeaderPlaceholderSvg = () =>
    import('@/components/svg/landing-page-builder/landing-page-section/header-placeholder-svg');
const FooterPlaceholderSvg = () =>
    import('@/components/svg/landing-page-builder/landing-page-section/footer-placeholder-svg');

export default {
    name: 'header-footer-details-widget',
    components: {
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'app-profile-image': AppProfileImage,
        'header-placeholder-svg': HeaderPlaceholderSvg,
        'footer-placeholder-svg': FooterPlaceholderSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: true,
            elementData: {
                name: '',
                previewImageUrl: '',
                vendor: {},
                categories: [],
                createdAt: '',
                publishedAt: '',
                updatedAt: '',
            },
        };
    },

    computed: {
        element() {
            return this.widget.element;
        },

        elementType() {
            return capitalize(this.element.type);
        },

        elementId() {
            return this.element.id;
        },

        isClient() {
            return !!this.$store.state.context.client.id;
        },

        isAgency() {
            return !!this.$store.state.context.agency.id;
        },
    },

    created() {
        this.getHeaderInfo();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getHeaderInfo() {
            this.isLoading = true;

            const requestConfig = {
                expand: 'Categories($select=Name)',
            };

            if (this.isClient) {
                requestConfig.expand =
                    requestConfig.expand +
                    ',Account($select=Id;$expand=Agency($select=Name,LogoImageUrl))';
            } else {
                requestConfig.expand = requestConfig.expand + ',SharedAssets($count=true;$top=0)';
            }

            return new Promise(() => {
                LandingPageSectionService.get(this.elementId, requestConfig)
                    .then(({ data }) => {
                        const element = data.value[0];
                        const isAgencyAvailable =
                            element.Account &&
                            element.Account.Agency &&
                            element.Account.Agency.Name;
                        let publishedDate = null;

                        if (element.PublishedAt) {
                            publishedDate = element.PublishedAt;
                        } else if (element['SharedAssets@odata.count'] > 0) {
                            publishedDate = element.SharedAssets[0].CreatedAt;
                        }
                        this.elementData = {
                            name: element.Name,
                            previewImageUrl: element.PreviewImageUrl,
                            categories: element.Categories.map(({ Name }) => Name),
                            isPublished:
                                element.IsPublished || element['SharedAssets@odata.count'] > 0,
                            vendor: isAgencyAvailable
                                ? {
                                      name: element.Account.Agency.Name,
                                      logoUrl: element.Account.Agency.LogoImageUrl,
                                  }
                                : null,
                            publishedTo: element['SharedAssets@odata.count'],
                            createdAt: this.$moment(element.CreatedAt),
                            updatedAt: this.$moment(element.UpdatedAt),
                            publishedAt: publishedDate ? this.$moment(publishedDate) : null,
                        };
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            `An error occurred while fetching your ${this.elementType} details.`,
                            { error },
                        );
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            });
        },
    },
};
</script>

<style>
.header-footer-details-widget .form-details-container {
    border-radius: 16px;
    box-shadow: 0px 3px 12px #8274e833;
}

.header-footer-details-widget .form-details-container .detail-container {
    padding: 16px 24px;
}

.header-footer-details-widget .form-details-container .detail-container:nth-child(odd) {
    background-color: #f7f7f8;
}

.header-footer-details-widget .detail-container .profile-container {
    align-items: center;
    display: flex;
    color: #18aeff;
}

.header-footer-details-widget
    .detail-container
    .profile-container
    .app-profile-image
    .background-image {
    height: 28px;
    margin-right: 10px;
    width: 28px;
}

.header-footer-details-widget .form-details-container .weekday {
    border-right: 2px solid #dcdbe0;
    margin-right: 10px;
    padding-right: 10px;
}

.header-footer-details-widget .form-details-container .categories-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
}

.header-footer-details-widget .form-details-container .category {
    background-color: #f7f7f8;
    border-radius: 18px;
    border: 1px solid #dcdbe0;
    display: flex;
    justify-content: center;
    margin: 0;
    padding: 7px 16px;
    text-align: center;
    width: fit-content;
    text-transform: capitalize;
}

.header-footer-details-widget .form-details-container .status-chip {
    border-radius: 18px;
    font: normal normal 500 14px Roboto;
    padding: 8px 25px;
    width: fit-content;
}

.header-footer-details-widget .form-details-container .published {
    background-color: #e2f4e3;
    color: #2ada2b;
}

.header-footer-details-widget .form-details-container .unpublished {
    background-color: #eb7e0a33;
    color: #eb7e0a;
}

.header-footer-details-widget .cancel-btn {
    background: transparent;
    border: none;
    color: #877d88;
}

.header-footer-details-widget .cancel-btn:hover {
    background: #e1e0e5;
}
</style>
